// ─── clinicalMaternalSafety.js ─────────────────────────────────────────
// V97.64 (migration Supabase éditable) — SOURCE UNIQUE des contraintes
// maternelles P0 (grossesse / allaitement), FR + EN.
//
// Auparavant : deux constantes hardcode séparées (MATERNAL_SAFETY_FR dans
// _clinicalContext.fr.js, MATERNAL_SAFETY_EN dans en.js). Extraites ici pour
//   (a) casser tout risque de cycle d'imports (ce module ne dépend d'AUCUN des
//       deux fichiers de prompt — l'inverse) ;
//   (b) offrir un point unique DB-éditable, même pattern que
//       clinicalInteractions.js / clinical_guardrails.
//
// P0 SÉCURITÉ-CRITIQUE : c'est le chemin qui garantit qu'une cliente enceinte
// reçoit ses contraintes. Le fallback hardcode est donc IMPÉRATIF et validé
// (contenu Anissa 12 juin 2026 + évictions listeria 2 juillet 2026). Toute
// panne DB (indisponible / vide / colonne manquante / valeur corrompue) retombe
// silencieusement sur ce hardcode — jamais de perte de contrainte.
//
// Post-partum : état DÉTECTÉ mais SANS bloc en V1 — décision Anissa (cliente
// standard). Absent du hardcode ET du seed DB : getActiveMaternalSafetyEntry
// renvoie null pour cet état, comme avant.

// ── HARDCODE FALLBACK (validé Anissa) ─────────────────────────────────
// Contenu clinique validé Anissa — Module 1, 12 juin 2026 (grossesse /
// allaitement) + évictions grossesse validées 2 juillet 2026 (doc V2, point 3).
// Formulations SCELLÉES. Toute modification repasse par Anissa.

// ── SOURCE OFFICIELLE DES ÉVICTIONS GROSSESSE ─────────────────────────
//
// Décision Anissa du 2026-07-31 : reprendre les 14 évictions de la brochure
// officielle SANS LES CONDENSER, et inscrire la source dans le code.
//
// Attention aux dates : la fiche produit de la boutique fédérale affiche
// encore « OSAV 2020 », métadonnée périmée. C'est l'impressum du PDF qui
// fait foi. Les dates affichées sur le site de l'OSAV à côté des PDF sont
// des dates de MISE EN LIGNE, pas d'édition.
//
// Détail de la comparaison ligne par ligne avec l'ancienne formulation :
// docs/OSAV-GROSSESSE-TABLEAU-COMPARATIF-2026-07-31.md
export const OSAV_GROSSESSE_SOURCE = Object.freeze({
  editeur: 'OSAV',
  titre: 'Alimentation pendant la grossesse et la période d\'allaitement',
  date: 'août 2025',
  reference: '341.801.F',
  page: 28,
  consulteLe: '2026-07-31',
  libelle: 'OSAV, Alimentation pendant la grossesse et la période d\'allaitement, août 2025, réf. 341.801.F',
});

/**
 * Les 14 évictions officielles, VERBATIM, dans l'ordre de la brochure
 * (p. 28, encadré « À ÉVITER DURANT VOTRE GROSSESSE »).
 *
 * NE PAS reformuler, NE PAS condenser, NE PAS réordonner : un test
 * (clinicalMaternalSafety.osav.test.js) verrouille la liste au caractère
 * près. Toute évolution passe par une nouvelle édition de la brochure ET
 * une validation d'Anissa.
 *
 * Source complémentaire pour le détail fromager (pâtes dures autorisées,
 * croûte à retirer, températures) : OSAV, « Recommandations et informations
 * de fond pour la consommation de lait et de produits laitiers durant la
 * grossesse », Berne, avril 2020, 4ᵉ version. Elle complète, ne remplace pas.
 */
export const PREGNANCY_EVICTIONS_OSAV_FR = Object.freeze([
  'Lait cru',
  'Produits au lait cru, comme le beurre ou le séré au lait cru (vérifiez sur l\'étiquette)',
  'Fromages bleus (comme le gorgonzola)',
  'Fromages à pâte molle au lait cru, thermisé ou pasteurisé',
  'Fromages à pâte mi-dure au lait cru, thermisé ou pasteurisé',
  'Feta',
  'Viande crue (carpaccio, viande saignante ou à point)',
  'Produits de charcuterie crus (gendarmes, schüblig, salami, salsiz, mettwurst) et jambon cru',
  'Poisson cru (sushis) et fruits de mer crus',
  'Poisson fumé comme le saumon fumé ou la truite fumée',
  'Aliments contenant des œufs crus (comme le tiramisu)',
  'Fruits de mer (comme les crevettes), même prêts à consommer (sauf s\'ils sont bien chauffés avant d\'être consommés)',
  'Salades et fruits prédécoupés et emballés',
  'Pousses',
]);

export const MATERNAL_SAFETY_FR = {
  grossesse: {
    label: 'GROSSESSE en cours',
    rules: [
      // ── Règles cliniques d'ANISSA (2026-06-12). Elles ne viennent PAS de
      //    l'OSAV et ne doivent jamais lui être attribuées.
      'Pas de jeûne (ni intermittent ni prolongé).',
      'Pas de restriction calorique agressive.',
      "Vigilance vitamine A (éviter les apports/compléments à forte dose et les sources concentrées comme le foie).",
      "Vigilance iode (besoins spécifiques — ne pas sur- ni sous-doser sans l'avis d'Anissa).",
      // ── Évictions officielles, reprises telles quelles avec leur source.
      `Évictions alimentaires systématiques (prévention listériose/toxoplasmose), liste officielle ${OSAV_GROSSESSE_SOURCE.libelle} — ne jamais proposer : ${PREGNANCY_EVICTIONS_OSAV_FR.join(' ; ')}.`,
    ],
  },
  allaitement: {
    label: 'ALLAITEMENT en cours',
    rules: [
      'Pas de restriction calorique agressive.',
      "Vigilance supplémentation (toute supplémentation à valider avec Anissa).",
    ],
  },
  // postPartum : volontairement absent en V1 (décision Anissa, cliente standard).
};

/**
 * Miroir anglais des 14 évictions. TRADUCTION de la liste officielle
 * française — pas une source distincte : la brochure OSAV n'existe qu'en
 * français, allemand et italien. La liste FR fait foi en cas de doute.
 *
 * POURQUOI on la traduit : laisser la version anglaise sur l'ancienne
 * formulation condensée créerait deux niveaux de protection selon la langue
 * de la cliente.
 *
 * VALIDÉE PAR ANISSA le 2026-07-31. Elle a repris 10 lignes telles quelles
 * et corrigé les lignes 2, 7, 8, 9 et 12. Ses arbitrages :
 *  - « quark (séré) » : terme anglais compréhensible, tout en gardant le nom
 *    lu sur les emballages suisses ;
 *  - les noms de charcuteries suisses restent en l'état, pour la même raison.
 * Comparatif : docs/OSAV-GROSSESSE-COMPARATIF-FR-EN-2026-07-31.md
 */
export const PREGNANCY_EVICTIONS_OSAV_EN = Object.freeze([
  'Raw milk',
  'Raw-milk products, such as butter or quark (séré) made from raw milk (check the label)',
  'Blue cheeses (such as gorgonzola)',
  'Soft cheeses made from raw, thermised or pasteurised milk',
  'Semi-hard cheeses made from raw, thermised or pasteurised milk',
  'Feta',
  'Raw or undercooked meat, including carpaccio and meat served rare or medium',
  'Uncooked cured meats (gendarmes, schüblig, salami, salsiz and mettwurst) and dry-cured ham',
  'Raw fish, including sushi, and raw shellfish',
  'Smoked fish such as smoked salmon or smoked trout',
  'Foods containing raw eggs (such as tiramisu)',
  'Shellfish, such as prawns, including ready-to-eat products, unless thoroughly heated before consumption',
  'Pre-cut and packaged salads and fruit',
  'Sprouts',
]);

export const MATERNAL_SAFETY_EN = {
  grossesse: {
    label: 'ONGOING PREGNANCY',
    rules: [
      // Anissa's own clinical rules — NOT from the OSAV, never attribute.
      // Wording harmonised by Anissa on 2026-07-31.
      'No intermittent or prolonged fasting.',
      'No aggressive calorie restriction.',
      'Vitamin A caution: avoid high intakes, high-dose supplements and concentrated sources such as liver.',
      "Iodine caution: requirements are specific during pregnancy; avoid excessive or insufficient intake without Anissa's guidance.",
      `Systematic food evictions (listeriosis/toxoplasmosis prevention), official list ${OSAV_GROSSESSE_SOURCE.libelle} — never propose: ${PREGNANCY_EVICTIONS_OSAV_EN.join(' ; ')}.`,
    ],
  },
  allaitement: {
    label: 'ONGOING BREASTFEEDING',
    rules: [
      // 2026-07-31 — « caloric » → « calorie », alignement sur le bloc
      // grossesse. Correction linguistique validée par Anissa, aucun
      // changement clinique.
      'No aggressive calorie restriction.',
      'Supplementation caution (any supplementation to be validated with Anissa).',
    ],
  },
  // postPartum: intentionally absent in V1 (Anissa's decision, standard client).
};

// ── CACHE DB SUPABASE (même pattern que clinicalInteractions.js) ───────
// _dbCache : { [state]: { fr: {label, rules}, en: {label, rules} } } | null.
let _dbCache = null;
let _dbCacheLoadedAt = 0;
const DB_CACHE_TTL_MS = 5 * 60 * 1000; // 5 min

/**
 * Précharge les contraintes maternelles depuis Supabase (table
 * clinical_maternal_safety). À appeler au mount d'un composant qui génère un
 * plan. Idempotent (un fetch par TTL). Fallback silencieux sur hardcode.
 *
 * @param {object} supabaseClient - Client Supabase (from('clinical_maternal_safety'))
 * @param {object} [opts]
 * @param {boolean} [opts.force=false]
 * @returns {Promise<{ ok: boolean, count?: number, source: 'supabase'|'hardcode', error?: string }>}
 */
export async function preloadMaternalSafetyFromSupabase(supabaseClient, opts = {}) {
  const { force = false } = opts;
  if (!force && _dbCache && (Date.now() - _dbCacheLoadedAt < DB_CACHE_TTL_MS)) {
    return { ok: true, count: Object.keys(_dbCache).length, source: 'supabase' };
  }
  if (!supabaseClient || typeof supabaseClient.from !== 'function') {
    return { ok: false, error: 'no supabase client', source: 'hardcode' };
  }
  try {
    const { data, error } = await supabaseClient
      .from('clinical_maternal_safety')
      .select('state, label_fr, rules_fr, label_en, rules_en')
      .eq('enabled', true);
    if (error || !Array.isArray(data) || data.length === 0) {
      return { ok: false, error: error?.message || 'empty table', source: 'hardcode' };
    }
    const map = {};
    for (const row of data) {
      // DÉFENSIF P0 : une ligne sans état, sans label ou sans règles
      // exploitables est IGNORÉE → l'état retombe sur le hardcode via le merge
      // ci-dessous. Jamais un bloc maternel vide/partiel injecté depuis la DB.
      if (!row.state) continue;
      const fr = validEntry(row.label_fr, row.rules_fr);
      const en = validEntry(row.label_en, row.rules_en);
      if (!fr || !en) continue; // les DEUX langues doivent être exploitables
      map[row.state] = { fr, en };
    }
    if (Object.keys(map).length === 0) {
      return { ok: false, error: 'no usable row', source: 'hardcode' };
    }
    _dbCache = map;
    _dbCacheLoadedAt = Date.now();
    return { ok: true, count: Object.keys(map).length, source: 'supabase' };
  } catch (e) {
    return { ok: false, error: e?.message || String(e), source: 'hardcode' };
  }
}

/** Une entrée { label, rules } n'est valide que label non-vide + rules non-vide. */
function validEntry(label, rules) {
  const l = typeof label === 'string' ? label.trim() : '';
  const r = Array.isArray(rules) ? rules.filter((x) => typeof x === 'string' && x.trim()) : [];
  if (!l || r.length === 0) return null;
  return { label: l, rules: r };
}

/** Vrai si le cache DB est frais. */
function dbFresh() {
  return _dbCache && (Date.now() - _dbCacheLoadedAt < DB_CACHE_TTL_MS);
}

/**
 * Entrée maternelle active { label, rules } pour un état + une langue.
 * DB si fraîche ET l'état y est exploitable ; sinon fallback hardcode. Renvoie
 * null si l'état n'a pas de bloc (ex. post-partum) — comportement iso au
 * `MATERNAL_SAFETY_X[state] || null` d'origine.
 *
 * FAIL-SAFE P0 : le fallback est PAR ÉTAT — si la DB fournit la grossesse mais
 * pas l'allaitement, l'allaitement retombe sur le hardcode, jamais à vide.
 *
 * @param {'grossesse'|'allaitement'|'postPartum'|string|null} state
 * @param {'fr'|'en'} locale
 * @returns {{ label: string, rules: string[] } | null}
 */
export function getActiveMaternalSafetyEntry(state, locale) {
  if (!state) return null;
  const lang = locale === 'en' ? 'en' : 'fr';
  if (dbFresh() && _dbCache[state] && _dbCache[state][lang]) {
    return _dbCache[state][lang];
  }
  const hardcode = lang === 'en' ? MATERNAL_SAFETY_EN : MATERNAL_SAFETY_FR;
  return hardcode[state] || null;
}

/** Reset le cache (tests). @internal */
export function _resetMaternalSafetyCache() {
  _dbCache = null;
  _dbCacheLoadedAt = 0;
}

/**
 * Source effective (debug/observabilité).
 * @returns {'supabase'|'hardcode'}
 */
export function getMaternalSafetySource() {
  return dbFresh() ? 'supabase' : 'hardcode';
}

/**
 * Lecture stricte pour le chemin MGD. La politique V97.209 est scellée pour
 * la génération FR uniquement ; aucune constante locale ne peut la suppléer.
 */
export function getMgdMaternalSafetyEntry(state, locale, manifest) {
  if (locale !== 'fr' || !manifest || manifest.ready !== true
      || manifest.registryKey !== 'mgd_plan_generation'
      || manifest.locale !== 'fr' || !Array.isArray(manifest.maternalPolicies)) {
    throw new Error('clinical_policy_not_ready');
  }
  const row = manifest.maternalPolicies.find((candidate) => candidate?.state === state);
  if (!row) return null;
  const label = typeof row.label_fr === 'string' ? row.label_fr.trim() : '';
  const rules = Array.isArray(row.rules_fr)
    ? row.rules_fr.filter((rule) => typeof rule === 'string' && rule.trim())
    : [];
  if (row.enabled !== true || !label || rules.length === 0) {
    throw new Error('clinical_policy_incomplete');
  }
  return Object.freeze({ label, rules: Object.freeze([...rules]) });
}
